import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Link } from "react-router-dom";

const EXAM_LABEL = { LGS: "LGS", YKS: "YKS", TYT: "TYT", AYT: "AYT" };
const SESSION_LABEL = { TYT: "TYT", AYT: "AYT", BOTH: "TYT + AYT" };
const EDU_LABEL = {
  "9": "9. Sınıf", "10": "10. Sınıf", "11": "11. Sınıf",
  "12": "12. Sınıf", GRADUATE: "Mezun", OTHER: "Diğer",
};

export default function MathMap() {
  const [map, setMap] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/analytics/map/topics");
        // Yakın Zaman: mevcut /analytics/me/map konu metriğini (recent_accuracy)
        // konu kartlarına topic_id ile birleştir. Hesaplama backend'de hazır;
        // burada yeni hesaplama yapılmaz.
        let recentByTopic = {};
        try {
          const res = await api.get("/analytics/me/map");
          for (const t of res.data?.topics || []) {
            recentByTopic[t.topic_id] = t.recent_accuracy;
          }
        } catch { /* Yakın Zaman gösterimi opsiyoneldir */ }
        setMap({
          ...data,
          topics: (data.topics || []).map((t) => ({
            ...t,
            recent_accuracy: recentByTopic[t.topic_id] ?? null,
          })),
        });
      } catch { setMap({ topics: [] }); }
    })();
  }, []);

  if (!map) return <div className="text-sm text-slate-500">Yükleniyor…</div>;

  const topics = map.topics || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl tracking-tight text-[#0B1A35]">Matematik Haritan</h1>
        <p className="text-slate-500 text-sm">Konu ve alt konu bazında güncel performansın.</p>
      </div>
      <div className="text-xs text-slate-400">
        <Link to="/profil" className="text-[#007BFF] hover:underline">Son 50 Yanlışım</Link>
      </div>

      {topics.length === 0 ? (
        <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl p-6 text-center">
          Henüz konu bazlı veri yok. Bir kaç soru çözdüğünde harita burada dolmaya başlayacak.
        </div>
      ) : (
        <div className="grid gap-3">
          {topics.map((t) => (
            <TopicCard key={t.topic_id} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function TopicCard({ t }) {
  const [open, setOpen] = useState(false);
  const acc = t.accuracy ?? 0;
  const band = acc >= 70 ? "bg-emerald-500" : acc >= 40 ? "bg-[#FFC107]" : "bg-rose-500";
  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden" data-testid={`map-topic-${t.topic_id}`}>
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left">
        <div>
          <div className="font-medium text-[#0B1A35]">{t.name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span data-testid={`map-topic-overall-${t.topic_id}`}>
              Genel Başarı: <span className="font-mono text-[#0B1A35]">{acc}%</span>
            </span>
            {t.recent_accuracy != null && (
              <span data-testid={`map-topic-recent-${t.topic_id}`}>
                Yakın Zaman: <span className="font-mono text-[#007BFF]">{t.recent_accuracy}%</span>
              </span>
            )}
            <span className="text-slate-400">{t.attempts} soru</span>
          </div>
        </div>
        <div className="w-24 h-2 rounded-full bg-slate-100 overflow-hidden shrink-0 ml-3">
          <div className={`h-full ${band}`} style={{ width: `${acc}%` }}/>
        </div>
      </button>
      {open && (
        <div className="border-t border-[#E2E8F0] p-3 space-y-2 bg-slate-50/50">
          {(t.subtopics || []).length === 0 ? (
            <div className="text-xs text-slate-400 italic">Alt konu verisi yok.</div>
          ) : (
            t.subtopics.map((s) => (
              <div key={s.subtopic_id} className="flex items-center justify-between px-2 py-1.5">
                <div>
                  <div className="text-sm">{s.name || "-"}</div>
                  <div className="text-[10px] text-slate-500">{s.attempts} soru yanıtlandı</div>
                </div>
                <AccuracyBar accuracy={s.accuracy} compact />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function AccuracyBar({ accuracy, compact }) {
  // Insufficient data → em-dash, no coloured bar.
  if (accuracy === null || accuracy === undefined) {
    return (
      <div className="flex items-center gap-3">
        <div className={`${compact ? "w-20" : "w-28"} h-2 rounded-full bg-slate-100`} />
        <div className={`text-sm font-mono ${compact ? "w-10" : "w-14"} text-right text-slate-400`}>—</div>
      </div>
    );
  }
  const band = accuracy >= 70 ? "bg-emerald-500" : accuracy >= 40 ? "bg-[#FFC107]" : "bg-rose-500";
  return (
    <div className="flex items-center gap-3">
      <div className={`${compact ? "w-20" : "w-28"} h-2 rounded-full bg-slate-100 overflow-hidden`}>
        <div className={`h-full ${band}`} style={{ width: `${accuracy}%` }} />
      </div>
      <div className={`text-sm font-mono ${compact ? "w-10" : "w-14"} text-right`}>{accuracy}%</div>
    </div>
  );
}

// Re-export label constants for other pages that render profile-scoped fields.
export { EXAM_LABEL, SESSION_LABEL, EDU_LABEL };
